import * as Yup from "yup";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  createProductApi,
  getParentProductNameListApi,
} from "../../services/_product";
import { errorToast, successToast } from "../../components/Toast";

const CreateProduct = () => {
  const navigate = useNavigate();
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [productType, setProductType] = useState<"PRODUCT" | "VARIETY">(
    "PRODUCT",
  );
  const [parentProducts, setParentProducts] = useState<any[]>([]);
  const createProductInitialValues = {
    name: "",
    parent_product_id: "",
    variety: "",
    hsn_no: "",
    status: "",
    description: "",
  };

  useEffect(() => {
    if (productType === "VARIETY") {
      fetchParentProducts();
    }
  }, [productType]);

  const fetchParentProducts = async () => {
    const result: any = await getParentProductNameListApi();
    if (result.isSuccess) {
      setParentProducts(result.data || []);
    }
  };

  const createProductValidationSchema = Yup.object().shape({
    name:
      productType === "PRODUCT"
        ? Yup.string().required("Product name is required")
        : Yup.string(),
    parent_product_id:
      productType === "VARIETY"
        ? Yup.string().required("Parent product is required")
        : Yup.string(),
    variety:
      productType === "VARIETY"
        ? Yup.string().required("Variety is required")
        : Yup.string(),
    description: Yup.string(),
    hsn_no: Yup.string(),
    status: Yup.string().required("Status is required"),
  });

  const handleSubmit = async (values: any, { resetForm }: any) => {
    setSubmitLoader(true);
    // const updatedValues = {
    //   ...values,
    //   status: values.status === "true",
    // };
    const payload: any = {
      type: productType,
      status: values.status === "true",
      hsn_no: values.hsn_no,
      description: values.description,
    };

    if (productType === "PRODUCT") {
      payload.name = values.name;
    } else {
      const parent = parentProducts.find(
        (p) => p.id === values.parent_product_id,
      );
      payload.name = parent?.name;
      payload.parent_product_id = values.parent_product_id;
      payload.variety = values.variety;
    }

    const result: any = await createProductApi(payload);

    if (result.isSuccess) {
      setSubmitLoader(false);
      resetForm({ values: createProductInitialValues });
      successToast(result.message);
      navigate("/product-list");
    } else {
      setSubmitLoader(false);
      errorToast(result.message);
    }
    return;
  };

  return (
    <>
      <div className="card">
        <div className="card-header pb-0">
          <div>
            <h5>Create Product</h5>
          </div>
        </div>
        <div className="card-body">
          <Formik
            initialValues={createProductInitialValues}
            validationSchema={createProductValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange }) => (
              <Form className="dt_adv_search">
                <div className="row">
                  <div className="col-12">
                    <div className="row g-3">
                      {/* PRODUCT TYPE */}
                      <div className="col-12">
                        <label
                          className="form-label d-block"
                          style={{ fontSize: "0.9375rem" }}
                        >
                          Product Type :
                        </label>
                        <div
                          className="rounded px-3 py-2 d-inline-block"
                          style={{ border: "1px solid #c1c1c1" }}
                        >
                          <div className="d-flex gap-4">
                            <div>
                              <input
                                type="radio"
                                checked={productType === "PRODUCT"}
                                onChange={() => setProductType("PRODUCT")}
                              />{" "}
                              Product
                            </div>
                            <div>
                              <input
                                type="radio"
                                checked={productType === "VARIETY"}
                                onChange={() => setProductType("VARIETY")}
                              />{" "}
                              Variety
                            </div>
                          </div>
                        </div>
                      </div>

                      {productType === "PRODUCT" ? (
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="name"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Name: <span className="text-danger">*</span>
                          </label>
                          <Field
                            id="name"
                            name="name"
                            type="text"
                            className="form-control"
                            placeholder="Enter name"
                            onChange={handleChange}
                          />
                          <ErrorMessage
                            name="name"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                      ) : (
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="parent_product_id"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Name: <span className="text-danger">*</span>
                          </label>
                          <select
                            id="parent_product_id"
                            name="parent_product_id"
                            className="form-select"
                            value={values.parent_product_id}
                            onChange={handleChange}
                          >
                            <option value="">Select Parent Product</option>
                            {parentProducts.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name}
                              </option>
                            ))}
                          </select>
                          <ErrorMessage
                            name="parent_product_id"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                      )}
                      {/* VARIETY */}
                      {productType === "VARIETY" && (
                        <div className="col-12 col-sm-6 col-lg-4">
                          <label
                            htmlFor="variety"
                            className="form-label"
                            style={{ fontSize: "0.9375rem" }}
                          >
                            Variety: <span className="text-danger">*</span>
                          </label>
                          <Field
                            id="variety"
                            name="variety"
                            type="text"
                            className="form-control"
                            placeholder="Enter Variety"
                            onChange={handleChange}
                          />
                          <ErrorMessage
                            name="variety"
                            className="text-danger"
                            component="div"
                          />
                        </div>
                      )}
                      {/* HSN */}
                      <div className="col-12 col-sm-6 col-lg-4">
                        <label
                          htmlFor="hsn_no"
                          className="form-label"
                          style={{ fontSize: "0.9375rem" }}
                        >
                          Hsn No:
                        </label>
                        <Field
                          id="hsn_no"
                          name="hsn_no"
                          type="text"
                          className="form-control"
                          placeholder="Enter hsn no"
                          onChange={handleChange}
                        />
                        <ErrorMessage
                          name="hsn_no"
                          className="text-danger"
                          component="div"
                        />
                      </div>
                      {/* STATUS */}
                      <div className="col-12 col-sm-6 col-lg-4">
                        <label
                          htmlFor="status"
                          className="form-label"
                          style={{ fontSize: "0.9375rem" }}
                        >
                          Status: <span className="text-danger">*</span>
                        </label>
                        <select
                          id="status"
                          name="status"
                          className="form-select"
                          value={values.status}
                          onChange={handleChange}
                        >
                          <option value="">Select Status</option>
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                        <ErrorMessage
                          name="status"
                          className="text-danger"
                          component="div"
                        />
                      </div>
                      {/* DESCRIPTION */}
                      <div className="col-12">
                        <label
                          htmlFor="description"
                          className="form-label"
                          style={{ fontSize: "0.9375rem" }}
                        >
                          Description:
                        </label>
                        <Field
                          id="description"
                          name="description"
                          type="text"
                          className="form-control"
                          placeholder="Enter description"
                          onChange={handleChange}
                          as={"textarea"}
                          rows={2}
                        />
                        <ErrorMessage
                          name="description"
                          className="text-danger"
                          component="div"
                        />
                      </div>
                      {/* ACTIONS */}
                      <div className="col-12">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={submitLoader}
                        >
                          {submitLoader && (
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          )}
                          Submit
                        </button>
                        <Link
                          to={"/product-list"}
                          type="button"
                          className="btn btn-label-github ms-5"
                        >
                          Back
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default CreateProduct;
